import React, { useRef, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { displayPathAnalyticsTime, shortDisplayTime, addUnitsFromDate } from '../../../../../../../utils/displayTime';
import { am4core, am4charts } from '../../../../../../../loaders/amchartsLoader';
import { colors } from '../../../../../../../utils/colors';
import { charCircle } from '../../../../../../../utils/enums';
import { format } from 'date-fns';
import { exportCSV } from '../../../../../../../utils/chart';
import { initChartThemeSupport } from '../../../../../../../common/Theme';
import { enableScrollbarGripKeyboard } from "../../../common/adUtils";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { useI18n } from "../../../../../../../i18n/useI18n";

const [HbrIconButton, HbrTooltip] = reactWrapper(["hbr-icon-button", "hbr-tooltip"]);
const ADBarChart = (props) => {
    const { tViews } = useI18n('views');
    const currentChartRef = useRef();
    const [preSelected, setPreSelected] = useState(props.selectedBar);
    const [timeStampLabel, setTimeStampLabel] = useState();
    
    const createBarChart = () => {
        let chart = am4core.create(props.id, am4charts.XYChart);
        currentChartRef.current = chart;
        chart.data = props.data;

        // Add Export
        chart.paddingRight = 40;

        const dateAxisX = chart.xAxes.push(new am4charts.DateAxis());
        dateAxisX.baseInterval.timeUnit = "hour";
        dateAxisX.baseInterval.count = 1;
        dateAxisX.tooltip.disabled = true;
        dateAxisX.renderer.grid.template.location = 0;
        dateAxisX.renderer.labels.template.wrap = true;
        dateAxisX.renderer.labels.template.maxWidth = 100;
        dateAxisX.renderer.labels.template.truncate = false;
        dateAxisX.renderer.inside = false;
        dateAxisX.renderer.labels.template.horizontalCenter = "right";
        dateAxisX.renderer.labels.template.verticalCenter = "middle";

        const valueAxisY = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxisY.cursorTooltipEnabled = false;
        valueAxisY.min = 0;
        valueAxisY.title.text = props.titleY;
        valueAxisY.extraMax = 0.1;
        valueAxisY.renderer.minGridDistance = 60;
        let series
        if(props?.data?.length > 0) {
            let keyList = props?.data?.map(d => {
              return Object.keys(d);
            });
            let noOfSeries = [...new Set(keyList.flat())];
            noOfSeries.forEach((key) => {
                if(key === "entry_ts") return;
                series = chart.series.push(new am4charts.ColumnSeries());
                series.dataFields.valueY = key;
                series.dataFields.dateX = "entry_ts";
                series.name = key;
                series.columns.template.tooltipText = "{name}: [bold]{valueY}[/]";
                series.stacked = true;
                series.showOnInit = false;
                series.fill = colors.lavender45;
                series.stroke = colors.lavender45;

                var columnTemplate = series.columns.template;
                columnTemplate.width = am4core.percent(50);

                var activeState = columnTemplate.states.create("active");
                activeState.properties.stroke = am4core.color(colors.gray0);
                activeState.properties.strokeWidth = 2;

                columnTemplate.events.on("hit", function onColumnHit(ev) {
                    let selectedDate = displayPathAnalyticsTime(ev.target.column._dataItem.dates.dateX);
                    props.saveData(selectedDate);
                    setPreSelected(selectedDate);
                    highlighColumn(selectedDate);
                });

                // Tooltip colors will be set dynamically by applyChartDarkTheme

                // Use adapter to format tooltip text
                series.columns.template.adapter.add("tooltipText", function (text, target) {
                    let dataItem = target.dataItem;
                    let date = format(dataItem.dateX, "yyyy-MM-dd, h:mm a");
                    let anomaly_rate = dataItem.dataContext[`${tViews('predictiveNetworks.anomalyRateOnly')}`];
                    text = `[bold]${date}[/]\n\n`;
                    text = text + `[${colors.lavender45}]${charCircle}[/] ${tViews('preSpace')} ${tViews('anomalyRatewithColon')} ${tViews('preSpace')} ${anomaly_rate} %\n\n`;
                    return text;
                });
            });
        }

        // Hightlight selected bar
        series.events.on("ready", () => {
            highlighColumn(preSelected);
        })

        //Add legends
        chart.legend = new am4charts.Legend();
        chart.legend.position = "bottom";
        chart.legend.paddingBottom = 8;
        chart.legend.contentAlign = "right";
        // Add tooltip for legend items
        chart.legend.itemContainers.template.tooltipText = tViews('clickToToggle');
        chart.legend.itemContainers.template.tooltipPosition = "pointer";

        //Add scrollbar
        chart.scrollbarX = new am4charts.XYChartScrollbar();
        chart.zoomOutButton.disabled = true;
        chart.scrollbarX.background.fill = am4core.color(colors.blue70);
        chart.scrollbarX.startGrip.visible = true;
        chart.scrollbarX.endGrip.visible = true;
        chart.scrollbarX.disabled = false;
        chart.scrollbarX.thumb.minWidth = 10;
        const startLine = chart.scrollbarX.startGrip.createChild(am4core.Rectangle);
        startLine.height = 30;
        startLine.width = 5;
        startLine.fillOpacity = 0.4;
        startLine.align = "center";
        startLine.valign = "middle";
        const endLine = chart.scrollbarX.endGrip.createChild(am4core.Rectangle);
        endLine.height = 30;
        endLine.width = 5;
        endLine.fillOpacity = 0.4;
        endLine.align = "center";
        endLine.valign = "middle";
        chart.scrollbarX.background.fillOpacity = 0.5;
        if(series) {
            chart.scrollbarX.series.push(series);
            var scrollSeries = chart.scrollbarX.scrollbarChart.series.getIndex(0);
            scrollSeries.strokeDasharray = "2,4";
        }
        chart.scrollbarX.parent = chart.bottomAxesContainer;
        chart.scrollbarX.minHeight = 30;
        chart.scrollbarX.thumb.minWidth = 50;
        enableScrollbarGripKeyboard(chart.scrollbarX, {
            insertBeforeChart: true,
            labelPrefix: props.titleY
        });

        customizeGrip(chart.scrollbarX.startGrip);
        customizeGrip(chart.scrollbarX.endGrip);

        // Customize scrollbar thumb
        chart.scrollbarX.background.fill = am4core.color(colors.blue45);
        chart.scrollbarX.background.fillOpacity = 0.5;

        const scrollbarDateAxis = chart.scrollbarX?.scrollbarChart?.xAxes?.getIndex(0);
        if (scrollbarDateAxis) {
            scrollbarDateAxis.renderer.minGridDistance = 14;
            scrollbarDateAxis.renderer.labels.template.fontSize = 12;
            scrollbarDateAxis.dateFormats.setKey("hour", "");
            scrollbarDateAxis.periodChangeDateFormats.setKey("hour", "");
            scrollbarDateAxis.dateFormats.setKey("day", "MMM dd");
            scrollbarDateAxis.periodChangeDateFormats.setKey("day", "MMM dd");
            scrollbarDateAxis.dateFormats.setKey("month", "MMM");
            scrollbarDateAxis.periodChangeDateFormats.setKey("month", "MMM");
            scrollbarDateAxis.periodChangeDateFormats.setKey("year", "MMM yyyy");
            scrollbarDateAxis.renderer.labels.template.adapter.add("textOutput", (text, target) => {
                const date = target?.dataItem?.date;
                const index = target?.dataItem?.index;

                if (!date) {
                    return text;
                }

                if (index === 0) {
                    return scrollbarDateAxis.dateFormatter.format(date, "MMM dd");
                }

                return date.getHours() === 12
                    ? scrollbarDateAxis.dateFormatter.format(date, "MMM dd")
                    : "";
            });
        }
        setTimeStampLabel(shortDisplayTime(props.selectedBar) + " - " + shortDisplayTime(addUnitsFromDate(new Date(props.selectedBar), 1, "h")));

        const highlighColumn = (selectedDate) => {
            chart.series.each(function(series) {
            if (series instanceof am4charts.ColumnSeries) {
                series.columns.each(function(column) {
                if(selectedDate) {
                    if(displayPathAnalyticsTime(column.dataItem.dateX) !== selectedDate) {
                        column.setState("default");
                        column.isActive = false;
                    } else {
                        column.isActive = true;
                    }
                }
                })
            }
            })
        }
        initChartThemeSupport(chart, am4core, props);
        return chart;

    }

    useEffect(() => {
        if (currentChartRef.current) {
            currentChartRef.current.dispose();
        }
        let chart2Obj = createBarChart();
        return () => {
            chart2Obj?.dispose();
            chart2Obj?.events?.disable();
          };
    }, [props.data]);

    useEffect(() => {
        return () => {
            if (currentChartRef.current) {
            currentChartRef.current.dispose();
            }
            am4core.disposeAllCharts();
        };
    }, []);

    // Style scrollbar
    const customizeGrip = grip => {
        // Disable background
        grip.background.disabled = true;

        // Add vertical bar
        let line = grip.createChild(am4core.Rectangle);
        line.height = 30;
        line.width = 3;
        line.fill = colors.gray80;
        line.fillOpacity = 0.4;
        line.align = "center";
        line.valign = "middle";
    };

    const onDownloadCsvClick = () => {
        if (currentChartRef.current && props.id) {
            let id = props.id;
            if(id === "ad-site-chart") {
                id = tViews('adSiteChart');
            }
            exportCSV(currentChartRef.current, id);
        }
    };

    return (
        <div>
            <div id="usage-based-ad-download-csv">
                <HbrTooltip content={tViews('downloadCSV')}>
                    <span>
                    <HbrIconButton
                        size="20px"
                        name="file-csv"
                        data-testid="DownloadCsvBtn" 
                        onClick={() => onDownloadCsvClick()} 
                        arialabel={tViews('downloadChartDisplaying') + tViews('anomalyRateChart') + tViews('downloadCSVInfo') + timeStampLabel}
                    />
                    </span>
                </HbrTooltip> 
            </div>
            <div id={props.id} className="ad-bar-chart">
                <h1>BarChart</h1>
            </div>
        </div>)
}

ADBarChart.propTypes = {
    data: PropTypes.array,
    saveData: PropTypes.func,
    selectedBar: PropTypes.string,
    id: PropTypes.string,
    titleY: PropTypes.string,
    selectType: PropTypes.string
}

export default ADBarChart;
